import type { EngageTaskSignal } from '@/store/features/engage.feature';

/** How a signal reads: its text and its chip tone. */
export type SignalView = { text: string; tone: string };

const TONE = {
  green:
    'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300',
  blue: 'border-primary/25 bg-primary/5 text-primary dark:bg-primary/10',
  amber:
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-300',
  violet:
    'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-500/25 dark:bg-violet-500/10 dark:text-violet-300',
  muted:
    'border-gray-200 bg-gray-50 text-muted-foreground dark:border-white/[0.08] dark:bg-white/[0.03]',
};

/** The signals in the order the queue ranks them, best first. */
const SIGNALS: Record<EngageTaskSignal, SignalView> = {
  positive_reply: { text: 'Positive reply', tone: TONE.green },
  reply: { text: 'Reply', tone: TONE.blue },
  open_deal: { text: 'Open deal', tone: TONE.amber },
  job_change: { text: 'Job change', tone: TONE.violet },
};

const ORDER = Object.keys(SIGNALS);

/** The text and tone a signal shows with. */
export function signalView(signal: EngageTaskSignal): SignalView {
  // A signal a newer API sends still shows, as itself.
  return SIGNALS[signal] ?? { text: signal, tone: TONE.muted };
}

/** The best tier among a task's signals, 0 first; null with none. */
export function signalTier(
  signals: readonly EngageTaskSignal[] | undefined
): number | null {
  const tiers = (signals ?? []).map((signal) => ORDER.indexOf(signal));
  const known = tiers.filter((tier) => tier >= 0);
  return known.length ? Math.min(...known) : null;
}
